import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PaywallModal from '../components/PaywallModal'
import { collection, query, where, orderBy, getDocs, getDoc, doc } from 'firebase/firestore'
import { db } from '../utils/firebase'
import './ResourcesPage.css'
import {
  FileText,
  Download,
  Search,
  X,
  Phone,
  ExternalLink,
  Zap,
  Settings,
  BookOpen,
} from 'lucide-react'

function ResourcesPage() {
  const { userProfile, isSubscriptionActive } = useAuth()
  const isAdmin = userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [neighborhoodName, setNeighborhoodName] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all') // all | pdf | text
  const [pdfModal, setPdfModal] = useState({ open: false, url: '', title: '' })
  // Quick contact-info cards populated by admins in the Manage Resources
  // section. Rendered in the left sidebar so residents can grab a phone
  // number or open a link without hunting through documents.
  const [quickResources, setQuickResources] = useState([])

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadResources()
      loadQuickResources()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.neighborhoodId])

  const loadQuickResources = async () => {
    try {
      // Sort client-side — avoids needing a composite Firestore index
      // for (neighborhoodId + createdAt). The per-neighborhood list is
      // small, so in-memory sort is fine.
      const snap = await getDocs(query(
        collection(db, 'quickResources'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      ))
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      rows.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() || 0
        const bt = b.createdAt?.toMillis?.() || 0
        return bt - at
      })
      setQuickResources(rows)
    } catch (e) {
      console.error('Error loading quick resources:', e)
    }
  }

  const loadResources = async () => {
    try {
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || userProfile.neighborhoodId)
      }
    } catch (e) {
      console.error('Error loading neighborhood:', e)
    }

    try {
      setLoading(true)
      const resourcesQuery = query(
        collection(db, 'resources'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(resourcesQuery)
      const resourcesList = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date()
      }))

      setResources(resourcesList)
    } catch (error) {
      console.error('Error loading resources:', error)
    } finally {
      setLoading(false)
    }
  }

  const openPdfModal = (url, title) => {
    // Append a cache-busting param so Chrome doesn't reuse a stale 304
    // response from before we fixed the Storage object's contentType /
    // contentDisposition metadata.
    const bust = `v=${Date.now()}`
    const separator = url.includes('?') ? '&' : '?'
    setPdfModal({ open: true, url: `${url}${separator}${bust}`, title })
  }

  const closePdfModal = () => setPdfModal({ open: false, url: '', title: '' })

  const filteredResources = resources
    .filter(r => {
      if (filterType === 'pdf') return !!r.pdfUrl
      if (filterType === 'text') return !r.pdfUrl
      return true
    })
    .filter(r => {
      if (!searchTerm) return true
      const s = searchTerm.toLowerCase()
      return r.title?.toLowerCase().includes(s) ||
             (r.content && r.content.toLowerCase().includes(s))
    })

  const locationLabel = neighborhoodName || userProfile?.neighborhoodId || 'your neighborhood'
  const hasAnyResource = resources.length > 0

  return (
    <div className="resources-page">
      {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}

      {/* Header — flat, left-aligned, no banner. */}
      <header className="resources-header">
        <div className="resources-header-text">
          <h1>Resources</h1>
          <p className="muted">
            Community links, guides, and docs for {locationLabel}
          </p>
        </div>
      </header>

      {/* Two-column layout: Quick Resources sidebar (sticky) on the left,
          full resources list on the right. Collapses below 900px. */}
      <div className="resources-layout">
        <aside className="resources-sidebar">
          <QuickResourcesPanel items={quickResources} isAdmin={isAdmin} />
        </aside>

        <div className="resources-main">
          {hasAnyResource && (
            <div className="resources-toolbar">
              <div className="resources-search">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search resources"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="resources-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="segmented" role="tablist" aria-label="Filter resources">
                {[
                  { v: 'all', label: 'All' },
                  { v: 'pdf', label: 'PDFs' },
                  { v: 'text', label: 'Text' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    className={`segmented-item${filterType === opt.v ? ' active' : ''}`}
                    onClick={() => setFilterType(opt.v)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="resources-loading">
              <div className="spinner" />
              <p className="muted">Loading resources…</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="card resources-empty">
              <div className="resources-empty-icon">
                <BookOpen size={32} />
              </div>
              <h3>{hasAnyResource ? 'No matching resources' : 'No resources yet'}</h3>
              <p className="muted">
                {hasAnyResource
                  ? 'Try clearing your search or switching filters.'
                  : `Community documents, guides, and links for ${locationLabel} will appear here.`}
              </p>
              {!hasAnyResource && isAdmin && (
                <Link to="/admin" className="btn-primary">
                  <Settings size={16} />
                  <span>Manage in Admin</span>
                </Link>
              )}
            </div>
          ) : (
            <div className="resources-list">
              {filteredResources.map(resource => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onOpenPdf={openPdfModal}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PDF Modal */}
      {pdfModal.open && (
        <div className="pdf-modal-overlay" onClick={closePdfModal}>
          <div className="pdf-modal" onClick={e => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <h3>{pdfModal.title}</h3>
              <div className="pdf-modal-actions">
                <a
                  href={pdfModal.url}
                  download
                  className="btn-primary small"
                  onClick={e => e.stopPropagation()}
                >
                  <Download size={16} />
                  <span>Download</span>
                </a>
                <button
                  type="button"
                  className="btn-ghost pdf-modal-close"
                  onClick={closePdfModal}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="pdf-modal-content">
              <iframe
                src={pdfModal.url}
                title={pdfModal.title}
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Resource card — .card.interactive with title, 2-line preview,
// optional PDF/Text badge in the corner, and a footer pill for
// "View PDF" or "Read more".
// ============================================================
function ResourceCard({ resource, onOpenPdf }) {
  const [expanded, setExpanded] = useState(false)
  const isPdf = !!resource.pdfUrl
  const hasContent = !!(resource.content && resource.content.trim())

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const handleAction = () => {
    if (isPdf) {
      onOpenPdf(resource.pdfUrl, resource.title)
    } else if (hasContent) {
      setExpanded(e => !e)
    }
  }

  return (
    <article className="card interactive resource-card" onClick={handleAction}>
      <div className="resource-card-head">
        <div className="resource-card-icon">
          <FileText size={20} />
        </div>
        <div className="resource-card-titlewrap">
          <h3 className="resource-card-title">{resource.title}</h3>
          <div className="resource-card-meta muted">
            {formatDate(resource.createdAt)}
          </div>
        </div>
        <span className={`badge resource-card-type ${isPdf ? 'is-pdf' : 'is-text'}`}>
          {isPdf ? 'PDF' : 'Text'}
        </span>
      </div>

      {hasContent && (
        <p className={`resource-card-preview${expanded ? ' expanded' : ''}`}>
          {resource.content}
        </p>
      )}

      {(isPdf || (hasContent && !expanded)) && (
        <div className="resource-card-footer">
          {isPdf ? (
            <span className="resource-card-cta">
              <FileText size={14} />
              <span>View PDF</span>
            </span>
          ) : (
            <span className="resource-card-cta">
              <span>Read more</span>
            </span>
          )}
        </div>
      )}
    </article>
  )
}

// ============================================================
// Quick Resources panel — sticky left sidebar. Each entry is a
// compact card with Zap icon, title, one-line description, and
// clickable phone / URL pill chips.
// ============================================================
function QuickResourcesPanel({ items, isAdmin }) {
  const hasItems = items && items.length > 0

  const formatPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '')
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
    }
    return raw
  }

  return (
    <div className="quick-resources-panel">
      <div className="quick-resources-heading">
        <h2>Quick Resources</h2>
        <p className="muted">Contacts and links at a glance.</p>
      </div>

      {hasItems ? (
        <div className="quick-resources-stack">
          {items.map(item => (
            <div key={item.id} className="card quick-resource-card">
              <div className="quick-resource-head">
                <div className="quick-resource-icon">
                  <Zap size={16} />
                </div>
                <div className="quick-resource-titlewrap">
                  <h4 className="quick-resource-title">{item.title}</h4>
                  {item.description && (
                    <p className="quick-resource-desc">{item.description}</p>
                  )}
                </div>
              </div>
              {(item.url || item.phone) && (
                <div className="quick-resource-chips">
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost small quick-resource-chip"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={14} />
                      <span>Open link</span>
                    </a>
                  )}
                  {item.phone && (
                    <a
                      href={`tel:${String(item.phone).replace(/[^\d+]/g, '')}`}
                      className="btn-ghost small quick-resource-chip"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone size={14} />
                      <span>{formatPhone(item.phone)}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="card quick-resources-empty">
          <div className="resources-empty-icon sm">
            <Zap size={20} />
          </div>
          <h4>No quick resources</h4>
          <p className="muted">Handy contacts and links will show here.</p>
          {isAdmin && (
            <Link to="/admin" className="btn-outline small">
              <Settings size={14} />
              <span>Manage in Admin</span>
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default ResourcesPage
